/** Screenshot-only jigsaw data helpers (copied from play stack — do not import /pages). */

export const JIGSAW_PLAY_TILE_SIZES = [6, 9, 12, 16, 24, 32];

/** Matches play UI (`jigsawBrowseUtils` / TemplateLevelGroup chip colors). */
const JIGSAW_DIFFICULTY_BY_TILE = {
    6: "EASY",
    9: "MEDIUM",
    12: "HARD",
    16: "PRO",
    24: "ELITE",
    32: "INSANE",
};

const JIGSAW_DIFFICULTY_LABELS = {
    EASY: "Easy",
    MEDIUM: "Medium",
    HARD: "Hard",
    PRO: "Pro",
    ELITE: "Elite",
    INSANE: "Insane",
};

function parseJigsawTileSizeFromName(name) {
    const trimmed = String(name ?? "").trim();
    if (!trimmed) return 0;
    const tilePart = (trimmed.split("-")[0] || "").trim();
    const match = tilePart.match(/^T(\d+)$/i);
    if (match) {
        const n = parseInt(match[1], 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
    }
    const legacy = parseInt(String(tilePart).replace(/^T0?/i, ""), 10);
    return Number.isFinite(legacy) && legacy > 0 ? legacy : 0;
}

function tileSizeFromPiecePaths(template) {
    const pieces =
        template?.piecePaths?.pieces ??
        template?.piece_paths?.pieces ??
        null;
    if (!Array.isArray(pieces) || pieces.length === 0) return 0;
    const n = Math.round(Math.sqrt(pieces.length));
    return n > 0 && n * n === pieces.length ? n : 0;
}

function tileSizeFromDifficultyLevel(level) {
    const key = String(level ?? "").toUpperCase();
    if (key === "EASY") return 6;
    if (key === "MEDIUM") return 9;
    if (key === "HARD") return 12;
    if (key === "PRO") return 16;
    if (key === "ELITE") return 24;
    if (key === "INSANE") return 32;
    return 0;
}

function isStandardJigsawPlayTileSize(tileSize) {
    return JIGSAW_PLAY_TILE_SIZES.includes(Number(tileSize));
}

export function getJigsawTemplateSeed(name) {
    const trimmed = String(name ?? "").trim();
    if (!trimmed) return "other";
    const parts = trimmed.split("-");
    if (parts.length >= 2 && parts[1].startsWith("S")) return parts[1];
    return "other";
}

export function pickPrimaryJigsawSeed(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const counts = {};
    for (const t of templates) {
        const seed = getJigsawTemplateSeed(t.templateName ?? t.template_name ?? t.name);
        counts[seed] = (counts[seed] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

export function resolveJigsawTemplateTileSize(template) {
    if (!template || typeof template !== "object") return 0;
    const name = String(
        template.templateName ?? template.template_name ?? template.name ?? "",
    ).trim();
    const fromName = parseJigsawTileSizeFromName(name);
    const direct = Number(template.tileSize ?? template.tile_size);
    const fromField = Number.isFinite(direct) && direct > 0 ? direct : 0;
    const fromPieces = tileSizeFromPiecePaths(template);
    const fromLevel = tileSizeFromDifficultyLevel(
        template.difficultyLevel ?? template.difficulty_level,
    );

    if (fromName > 0) return fromName;
    if (fromField > 0) {
        if (
            fromPieces > 0 &&
            fromPieces !== fromField &&
            isStandardJigsawPlayTileSize(fromPieces)
        ) {
            return fromPieces;
        }
        return fromField;
    }
    if (fromPieces > 0) return fromPieces;
    return fromLevel;
}

export function pickJigsawTemplateForTileSize(templates, tileSize, preferredSeed = null) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const ts = Number(tileSize);
    if (!Number.isFinite(ts) || ts <= 0) return null;
    if (preferredSeed) {
        const seeded = templates.find(
            (t) =>
                resolveJigsawTemplateTileSize(t) === ts &&
                getJigsawTemplateSeed(t.templateName ?? t.template_name ?? t.name) === preferredSeed,
        );
        if (seeded) return seeded;
    }
    return templates.find((t) => resolveJigsawTemplateTileSize(t) === ts) ?? null;
}

export function pickDefaultJigsawPlayTemplate(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const primarySeed = pickPrimaryJigsawSeed(templates);
    for (const tileSize of JIGSAW_PLAY_TILE_SIZES) {
        const match = pickJigsawTemplateForTileSize(templates, tileSize, primarySeed);
        if (match) return match;
    }
    return templates
        .slice()
        .sort((a, b) => resolveJigsawTemplateTileSize(a) - resolveJigsawTemplateTileSize(b))[0];
}

/**
 * Square art for screenshot / Google Ad capture.
 * Prefer icon400 over full `imageUrl` — originals often OOM or fail html-to-image
 * (notably Daily 06 boards and Gallery 08 multi-cover shots).
 */
export function pickJigsawSquareImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const keys = [
        "imageIcon400Url",
        "image_icon_400_url",
        "imageUrl",
        "image_url",
        "imageIcon200Url",
        "image_icon_200_url",
    ];
    for (const k of keys) {
        const u = story[k];
        if (typeof u === "string" && u.trim()) return u.trim();
    }
    return null;
}

/** Grid size → difficulty (same rules as play select page). */
export function inferJigsawDifficultyLevel(tileSize, explicitLevel) {
    const s = Number(tileSize);
    if (Number.isFinite(s) && s > 0 && JIGSAW_DIFFICULTY_BY_TILE[s]) {
        return JIGSAW_DIFFICULTY_BY_TILE[s];
    }
    const key = String(explicitLevel ?? "").toUpperCase();
    if (JIGSAW_DIFFICULTY_LABELS[key]) return key;
    return "MEDIUM";
}

/** Colored chip label + MUI `color` for screenshot title bar. */
export function getJigsawDifficultyDisplayInfo(tileSize, explicitLevel) {
    const level = inferJigsawDifficultyLevel(tileSize, explicitLevel);
    const label = JIGSAW_DIFFICULTY_LABELS[level] ?? JIGSAW_DIFFICULTY_LABELS.MEDIUM;
    if (level === "EASY") return { level, label, chipColor: "success" };
    if (level === "MEDIUM") return { level, label, chipColor: "warning" };
    if (level === "HARD") return { level, label, chipColor: "error" };
    if (level === "PRO") return { level, label, chipColor: "info" };
    if (level === "ELITE") return { level, label, chipColor: "secondary" };
    if (level === "INSANE") return { level, label, chipColor: "error" };
    return { level: "MEDIUM", label: JIGSAW_DIFFICULTY_LABELS.MEDIUM, chipColor: "warning" };
}
